import Filter from "@/Filter.class";
import { AutocompleteSuggestion } from "@/infrastructure/AutocompleteSuggestion";
import { IFilterProvider } from "@/interfaces/filter-providers/IFilterProvider";
import { FilterKey } from "@/models/FilterKey";
import { EventAggregationService } from "@/platform/EventAggregationService";
import { ref } from "vue";
import { Locations } from "./Locations";
import { ILocationFilterProvider } from "@/interfaces/filter-providers/ILocationFilterProvider";
import { PopularCountries } from "./PopularCountries";
import { AreaFilterSelection } from "../location/area/AreaFilterSelection";
import { SelectedLocations } from "./SelectedLocations";

export default class LocationV2Filter extends Filter {

	public readonly lastValidSelectionOption = ref('');
	public readonly selectionName = ref('');
	public readonly popularCountries: PopularCountries;
	public readonly selectedLocations: SelectedLocations;

	private readonly locations: Locations;
	private readonly locationFilterProvider: ILocationFilterProvider;

	public constructor(
		filterProvider: IFilterProvider,
		locationFilterProvider: ILocationFilterProvider,
		eventAggregationService?: EventAggregationService
	) {
		super(FilterKey.COUNTRY, filterProvider, eventAggregationService);

		this.locationFilterProvider = locationFilterProvider;

		this.locations = new Locations();
		this.selectedLocations = new SelectedLocations(filterProvider);
		this.popularCountries = new PopularCountries(filterProvider, locationFilterProvider);
	}

	public suggest(query: string): readonly AutocompleteSuggestion[] {
		return this.locations.getLocationsBy(query);
	}

	public async processSelection(value: string, key: FilterKey): Promise<void> {
		if (key === FilterKey.CONTINENT) {
			await this.filterProvider.clearFilters([FilterKey.AREA, FilterKey.COUNTRY]);
		}

		if (key === FilterKey.COUNTRY) {
			await this.filterProvider.clearFilter(FilterKey.CONTINENT);
		}

		if (key === FilterKey.AREA) {
			await this.processRegionSelection(value);
			return;
		}

		await this.filterProvider.processFilterSelection({
			key, value
		});
	}

	public async processCountryCheckboxSelection(): Promise<void> {
		await this.filterProvider.clearFilter(FilterKey.CONTINENT);
	}

	public async processAreaFilterChange(value: string): Promise<void> {
		const areaFilterSelection = new AreaFilterSelection(this.filterProvider, this.locationFilterProvider);
		return await areaFilterSelection.process(value);
	}

	private async processRegionSelection(value: string): Promise<void> {
		const continentFilterSelection = this.filterProvider.getFilterSelection(FilterKey.CONTINENT);
		const countryFilterSelection = this.filterProvider.getFilterSelection(FilterKey.COUNTRY);
		const countryId = this.locationFilterProvider.identifyCountryFor(value);

		const selection = [
			{
				key: FilterKey.AREA,
				value,
			},
		];

		if (!countryFilterSelection.includes(countryId)) {
			selection.push({
				key: FilterKey.COUNTRY,
				value: countryId,
			});
		}

		continentFilterSelection.forEach((continent) => {
			selection.push({
				key: FilterKey.CONTINENT,
				value: continent,
			});
		});

		await this.filterProvider.processFiltersSelection(selection);
	}
}
